import { inject, Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  notify,
  SiaCacheDatabaseService,
  SiaMenuService,
  SiaNewRouterService,
  SiaSessionService,
  SiaSignalrService,
  SiaTokenStoreService,
  SiaUsersService,
} from '@sia/shared';
import { DeviceDetectorService } from 'ngx-device-detector';
import { ReplaySubject, Subject } from 'rxjs';
import { AuthApi } from '../apis/auth.api';
import { SiaAuthResult } from '../interfaces/sia-auth-result';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  #api = inject(AuthApi);
  #router = inject(ActivatedRoute);
  #store = inject(SiaTokenStoreService);
  #userService = inject(SiaUsersService);
  #menuService = inject(SiaMenuService);
  #deviceService = inject(DeviceDetectorService);
  #cacheDatabaseService = inject(SiaCacheDatabaseService);
  #sessionService = inject(SiaSessionService);
  #routerService = inject(SiaNewRouterService);
  #signalrService = inject(SiaSignalrService);

  readonly #changePasswordPopUp = new Subject<boolean>();
  readonly #loggedUser = new ReplaySubject<void>();
  token = 'token';

  constructor() {
    // periodically refresh token
    setInterval(() => void this.refreshToken(), 60_000);
  }

  get changePasswordPopUp$() {
    return this.#changePasswordPopUp.asObservable();
  }
  get isLoggedIn$() {
    return this.#loggedUser.asObservable();
  }
  #createSiaAuthResult(result: {
    token?: string;
    errors?: any;
    messages?: any;
  }) {
    return {
      success: !!result.token,
      redirect: '/',
      token: result.token ?? '',
      errors: result.errors,
      messages: result.messages,
    } as SiaAuthResult;
  }

  public async refreshToken(): Promise<void> {
    const t = this.#store.getAccessToken();
    if (!t || !this.#store.isCurrentTokenExpiring()) {
      return;
    }

    try {
      const result: any = await this.#api.refreshToken(this.#store.getToken());
      if (result?.token) {
        this.#store.setToken(result.token);
      }
    } catch (err) {
      console.error(err);
    }
  }

  async refreshNotExpiringToken(): Promise<any> {
    try {
      const result: any = await this.#api.refreshToken(this.#store.getToken());
      if (result?.token) {
        this.#store.setToken(result.token);
      }
      return result;
    } catch (err) {
      console.error(err);
    }
  }

  async validateTotp(otp: string): Promise<boolean | void> {
    try {
      return await this.#api.validateTotp(otp);
    } catch {
      notify('Pin errato', 2000, 'error');
    }
  }

  async getNewPinOtp(userIdentifier: string): Promise<boolean | void> {
    try {
      return await this.#api.getNewPinOtp(userIdentifier);
    } catch {
      notify('Pin errato', 2000, 'error');
    }
  }

  async generateTotpQrCode(type: string): Promise<any> {
    try {
      const result = await this.#api.generateTotpQrCode(type);
      if (result) {
        return result;
      }
      notify('Credenziali errate', 2000, 'error');
    } catch {
      notify('Credenziali errate', 2000, 'error');
    }
  }

  // helper: attach device info to credentials
  #addDeviceInfo(credentials: any) {
    const di = this.#deviceService.getDeviceInfo();
    credentials.deviceInfo = {
      browser: di.browser,
      browserVersion: di.browser_version,
      device: di.device,
      deviceType: di.deviceType,
      orientation: di.orientation,
      os: di.os,
      osVersion: di.os_version,
      userAgent: di.userAgent,
    };
  }

  // helper: common post-login processing
  #handleLoginResult(result: any): SiaAuthResult | any {
    if (result?.token) {
      sessionStorage.setItem('instance-identifier', result.identifier);
      this.loggedUser();
      return this.#createLoginResult(result);
    }

    if (result?.twoFactorType) {
      sessionStorage.setItem('instance-identifier', result.identifier);
      return result;
    }

    notify('Credenziali errate', 2000, 'error');
    return {};
  }

  async login(credentials: any): Promise<SiaAuthResult> {
    this.#routerService.clearRoutes();
    this.#addDeviceInfo(credentials);

    try {
      const result: any = await this.#api.login(credentials);
      return this.#handleLoginResult(result);
    } catch {
      notify('Credenziali errate', 2000, 'error');
      return { token: null, success: false };
    }
  }

  #createLoginResult(token: any): SiaAuthResult {
    const r = this.#createSiaAuthResult(token);
    this.#store.setToken(r.token);
    return r;
  }

  async getStoredToken(): Promise<SiaAuthResult | null> {
    const token = this.#store.getRefreshToken();
    if (!token) {
      return null;
    }

    const result: any = await this.#api.getStoredToken(token);
    if (result?.token) {
      sessionStorage.setItem('instance-identifier', result.identifier);
      return this.#createLoginResult(result);
    }
    return null;
  }

  async logout(): Promise<boolean> {
    this.#signalrService.clearPostLoginRefreshGuard();
    this.#signalrService.emitLoggedUser(null);

    const done = await this.#api.logout();
    if (done) {
      this.#store.setToken(null);
    }
    return done;
  }

  async impersonate(userId: number): Promise<boolean> {
    this.#signalrService.clearPostLoginRefreshGuard();
    if (!this.#store.getAccessToken()) {
      return false;
    }

    try {
      const result: any = await this.#api.impersonate(
        this.#store.getToken(),
        userId,
      );
      if (!result?.token) {
        return false;
      }

      const di = this.#deviceService.getDeviceInfo();
      this.#store.setToken(result.token);
      const user = this.#userService.getUserData();
      if (!user) {
        return false;
      }

      const redirect =
        di.deviceType?.toLowerCase() === 'mobile'
          ? (user.redirectMobileMenuId ?? '')
          : (user.redirectMasterMenuId ?? '');

      this.#cacheDatabaseService.clearAllConfigs();
      this.#cacheDatabaseService.clearAllResponse();
      this.#cacheDatabaseService.clearAllActions();
      this.#routerService.clearRoutes();
      this.#sessionService.weakMemoryClearByContains('selectbox');
      this.#sessionService.clearStorage();
      await this.#menuService.reloadUserMenus();

      if (user.impersonated) {
        this.#routerService.navigateMenuWithParams(redirect);
      } else if (di.deviceType?.toLowerCase() === 'mobile') {
        this.#routerService.navigateMenuWithParams('list-users');
      } else {
        this.#routerService.navigateMenuWithParams('users');
      }

      return true;
    } catch (err) {
      console.error(err);
      return false;
    } finally {
      this.#userService.userChanged();
    }
  }

  requestPassword(data: any) {
    return this.#api.requestPassword(data);
  }

  getAccessToken() {
    return this.#store.getAccessToken();
  }

  restorePassword(strategy: string, data: any) {
    const t = this.#router.snapshot.queryParams[this.token];
    data.token = t;
    return this.#api.restorePassword(strategy, data);
  }

  resetPassword(pass: string, repass: string) {
    return this.#api.resetPassword(pass, repass);
  }

  changePasswordPopUp() {
    this.#changePasswordPopUp.next(true);
  }
  loggedUser() {
    this.#loggedUser.next();
  }
}
